import React, { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormField } from '@/components/form/FormField';
import { IMember } from '@/types/members.types';
import { IUserInfo } from '@/types/shared.types';
import { TEditContactForm } from '@/components/page/member-details/ContactDetails/types';
import { EditFormControls } from '@/components/common/profile/EditFormControls';
import { EditOfficeHoursFormControls } from '@/components/page/member-details/OfficeHoursDetails/components/EditOfficeHoursFormControls';
import { EditOfficeHoursMobileControls } from '@/components/page/member-details/OfficeHoursDetails/components/EditOfficeHoursMobileControls';
import Image from 'next/image';
import { omit } from 'lodash';
import { useMember } from '@/services/members/hooks/useMember';
import { useUpdateMember } from '@/services/members/hooks/useUpdateMember';
import { getProfileFromURL } from '@/utils/common.utils';
import { toSocialFieldInputValue } from '@/utils/profile/toSocialFieldInputValue';

import { CONTACT_VISIBILITY_KEYS } from './constants/contactVisibilityKeys';
import { isSharingContacts } from './utils/isSharingContacts';
import { buildEditContactSchema } from './formSchema';

import s from './EditContactForm.module.scss';
import { useMemberAnalytics } from '@/analytics/members.analytics';
import { toast } from '@/components/core/ToastContainer';
import { EditFormMobileControls } from '@/components/page/member-details/components/EditFormMobileControls';
import { clsx } from 'clsx';
import { useUpdateMemberPreferences } from '@/services/members/hooks/useUpdateMemberPreferences';
import { useUpdateEmail } from '@/services/members/hooks/useUpdateEmail';
import { FormSwitch } from '@/components/form/FormSwitch';
import { ContactDetailsVariant } from '@/components/page/member-details/ContactDetails';
import { isAdminUser } from '@/utils/user/isAdminUser';
import { getContactLogoByProvider } from '@/utils/profile/getContactLogoByProvider';

interface Props {
  onClose: () => void;
  member: IMember;
  userInfo: IUserInfo;
  linkedinRequired?: boolean;
  variant?: ContactDetailsVariant;
}

export const EditContactForm = ({ onClose, member, userInfo, linkedinRequired, variant }: Props) => {
  const router = useRouter();
  const schema = useMemo(() => buildEditContactSchema(!!linkedinRequired), [linkedinRequired]);

  const methods = useForm<TEditContactForm>({
    // Handles are stored bare, but the X / Telegram / Bluesky fields require a leading "@", so
    // seed those with it — otherwise re-saving an untouched profile fails validation.
    defaultValues: {
      telegram: toSocialFieldInputValue('telegram', member.telegramHandle),
      github: member.githubHandle,
      linkedin: member.linkedinHandle,
      discord: member.discordHandle,
      twitter: toSocialFieldInputValue('twitter', member.twitter),
      bluesky: toSocialFieldInputValue('bluesky', member.blueskyHandle),
      email: member.email,
      shareContacts: isSharingContacts(member.preferences),
    },
    resolver: yupResolver(schema),
  });
  const isAdmin = isAdminUser(userInfo);
  const isOwner = userInfo && userInfo.uid === member.id;
  const { handleSubmit, reset } = methods;
  const { mutateAsync } = useUpdateMember();
  const { mutateAsync: updatePreferences } = useUpdateMemberPreferences();
  const { data: memberData } = useMember(member.id);
  const { onSaveContactDetailsClicked } = useMemberAnalytics();

  const { requestEmailChange } = useUpdateEmail({
    uid: member.id,
    email: member.email,
    userInfo,
    source: 'member-profile',
  });

  const onSubmit = async (formData: TEditContactForm) => {
    onSaveContactDetailsClicked();

    if (!memberData) {
      return;
    }

    const preferencesPayload = {
      ...Object.fromEntries(CONTACT_VISIBILITY_KEYS.map((key) => [key, formData.shareContacts])),
      bluesky: formData.shareContacts,
      discord: formData.shareContacts,
      email: formData.shareContacts,
      github: formData.shareContacts,
      githubProjects: formData.shareContacts,
      linkedin: formData.shareContacts,
      telegram: formData.shareContacts,
      twitter: formData.shareContacts,
    };

    const payload = {
      participantType: 'MEMBER',
      referenceUid: member.id,
      uniqueIdentifier: member.email,
      newData: formatPayload(memberData.memberInfo, formData, isAdmin),
    };

    const prefRes = await updatePreferences({ uid: memberData.memberInfo.uid, payload: preferencesPayload });

    if (prefRes.isError) {
      return;
    }

    const res = await mutateAsync({
      uid: memberData.memberInfo.uid,
      payload,
    });

    if (!res.isError) {
      router.refresh();
      reset();
      onClose();
    } else if (res?.errorData?.message) {
      toast.error(res.errorData.message);
    }
  };

  const onEmailEdit = (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    requestEmailChange();
  };

  const Controls = variant === 'drawer' ? EditOfficeHoursFormControls : EditFormControls;

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Controls onClose={onClose} title="Edit Contact Details">
          <div className={s.body}>
            <div className={s.row}>
              <Image src={getContactLogoByProvider('email')} alt="Email" height={24} width={24} />
              <FormField name="email" label="Email" placeholder="Enter your email" disabled={isOwner} isRequired>
                {member.id === userInfo.uid && (
                  <button type="button" className={s.editEmailBtn} onClick={onEmailEdit}>
                    <EditIcon />
                  </button>
                )}
              </FormField>
            </div>
            <div className={s.row}>
              <Image src={getContactLogoByProvider('linkedin')} alt="Linkedin" height={24} width={24} />
              <FormField
                name="linkedin"
                label="LinkedIn"
                placeholder="eg., johndoe or https://linkedin.com/in/johndoe"
                isRequired={linkedinRequired}
              />
            </div>
            <div className={s.row}>
              <Image src={getContactLogoByProvider('telegram')} alt="Telegram" height={24} width={24} />
              <FormField name="telegram" label="Telegram" placeholder="eg., @username or https://t.me/username" />
            </div>
            {variant !== 'drawer' && (
              <div className={s.row}>
                <Image src={getContactLogoByProvider('github')} alt="Github" height={24} width={24} />
                <FormField name="github" label="Github" placeholder="eg., username or https://github.com/username" />
              </div>
            )}
            {variant !== 'drawer' && (
              <div className={s.row}>
                <Image src={getContactLogoByProvider('discord')} alt="Discord" height={24} width={24} />
                <FormField
                  name="discord"
                  label="Discord"
                  placeholder="eg., username or https://discord.com/users/username"
                />
              </div>
            )}
            <div className={s.row}>
              <Image src={getContactLogoByProvider('twitter')} alt="Twitter" height={24} width={24} />
              <FormField
                name="twitter"
                label="X (Twitter)"
                placeholder="eg., @protocollabs or https://twitter.com/protocollabs"
              />
            </div>
            <div className={s.row}>
              <Image src={getContactLogoByProvider('bluesky')} alt="Bluesky" height={24} width={24} />
              <FormField
                name="bluesky"
                label="Bluesky"
                placeholder="eg., @protocol.ai, protocol.ai or https://bsky.app/profile/protocol.ai"
              />
            </div>
            {variant !== 'drawer' && (
              <div className={clsx(s.row, s.center)}>
                <div className={s.switchLabelWrapper}>
                  <div className={s.switchLabel}>Show contact details to PL network members</div>
                  <div className={s.switchDesc}>Contact details are never displayed publicly</div>
                </div>
                <FormSwitch name="shareContacts" />
              </div>
            )}
          </div>
        </Controls>
        {variant === 'drawer' ? <EditOfficeHoursMobileControls /> : <EditFormMobileControls />}
      </form>
    </FormProvider>
  );
};

function formatPayload(memberInfo: any, formData: TEditContactForm, isAdmin: boolean) {
  // Normalize social handles - extract handles from URLs if provided
  const normalizedTwitter = formData.twitter ? getProfileFromURL(formData.twitter, 'twitter') : formData.twitter;
  const normalizedLinkedin = formData.linkedin ? getProfileFromURL(formData.linkedin, 'linkedin') : formData.linkedin;
  const normalizedDiscord = formData.discord ? getProfileFromURL(formData.discord, 'discord') : formData.discord;
  const normalizedGithub = formData.github ? getProfileFromURL(formData.github, 'github') : formData.github;
  const normalizedTelegram = formData.telegram ? getProfileFromURL(formData.telegram, 'telegram') : formData.telegram;
  const normalizedBluesky = formData.bluesky ? getProfileFromURL(formData.bluesky.trim(), 'bluesky') : formData.bluesky;

  return {
    imageUid: memberInfo.imageUid,
    name: memberInfo.name,
    email: isAdmin ? formData.email : memberInfo.email,
    plnStartDate: memberInfo.plnStartDate,
    city: memberInfo?.location?.city || '',
    region: memberInfo?.location?.region || '',
    country: memberInfo?.location?.country || '',
    teamOrProjectURL: memberInfo.teamOrProjectURL,
    linkedinHandler: normalizedLinkedin,
    discordHandler: normalizedDiscord,
    twitterHandler: normalizedTwitter,
    blueskyHandler: normalizedBluesky,
    githubHandler: normalizedGithub,
    telegramHandler: normalizedTelegram,
    moreDetails: memberInfo.moreDetails,
    openToWork: memberInfo.openToWork,
    plnFriend: memberInfo.plnFriend,
    teamAndRoles: memberInfo.teamMemberRoles,
    projectContributions: memberInfo.projectContributions?.map((contribution: any) => ({
      ...omit(contribution, 'projectName'),
    })),
    skills: memberInfo.skills?.map((skill: any) => ({
      title: skill.name ?? skill.title,
      uid: skill.id ?? skill.uid,
    })),
    customSkills: memberInfo.customSkills ?? [],
    bio: memberInfo.bio,
  };
}

const EditIcon = () => (
  <svg width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.3538 3.64664L12.3538 1.64664C12.3073 1.60016 12.2522 1.56328 12.1915 1.53811C12.1308 1.51295 12.0657 1.5 12 1.5C11.9343 1.5 11.8692 1.51295 11.8085 1.53811C11.7478 1.56328 11.6927 1.60016 11.6462 1.64664L5.64625 7.64664C5.59983 7.69311 5.56303 7.74827 5.53793 7.80897C5.51284 7.86967 5.49995 7.93471 5.5 8.00039V10.0004C5.5 10.133 5.55268 10.2602 5.64645 10.3539C5.74021 10.4477 5.86739 10.5004 6 10.5004H8C8.06568 10.5004 8.13073 10.4876 8.19143 10.4625C8.25212 10.4374 8.30728 10.4006 8.35375 10.3541L14.3538 4.35414C14.4002 4.30771 14.4371 4.25256 14.4623 4.19186C14.4874 4.13116 14.5004 4.0661 14.5004 4.00039C14.5004 3.93469 14.4874 3.86962 14.4623 3.80892C14.4371 3.74822 14.4002 3.69308 14.3538 3.64664ZM7.79313 9.50039H6.5V8.20727L10.5 4.20727L11.7931 5.50039L7.79313 9.50039ZM12.5 4.79352L11.2069 3.50039L12 2.70727L13.2931 4.00039L12.5 4.79352ZM14 7.50039V13.0004C14 13.2656 13.8946 13.52 13.7071 13.7075C13.5196 13.895 13.2652 14.0004 13 14.0004H3C2.73478 14.0004 2.48043 13.895 2.29289 13.7075C2.10536 13.52 2 13.2656 2 13.0004V3.00039C2 2.73518 2.10536 2.48082 2.29289 2.29329C2.48043 2.10575 2.73478 2.00039 3 2.00039H8.5C8.63261 2.00039 8.75979 2.05307 8.85355 2.14684C8.94732 2.24061 9 2.36779 9 2.50039C9 2.633 8.94732 2.76018 8.85355 2.85395C8.75979 2.94771 8.63261 3.00039 8.5 3.00039H3V13.0004H13V7.50039C13 7.36779 13.0527 7.24061 13.1464 7.14684C13.2402 7.05307 13.3674 7.00039 13.5 7.00039C13.6326 7.00039 13.7598 7.05307 13.8536 7.14684C13.9473 7.24061 14 7.36779 14 7.50039Z"
      fill="#8897AE"
    />
  </svg>
);
